import { Platform } from 'react-native';
import { File, Paths } from 'expo-file-system';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';

import type { Profile } from '@/lib/database.types';
import { formatAmount, formatDate } from '@/lib/format';
import { computeLineItemsTotals, lineItemTotal, type Offer, type OfferLineItem } from '@/lib/offers';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

async function uriToBase64(uri: string): Promise<string | null> {
  try {
    if (Platform.OS === 'web') {
      const res = await fetch(uri);
      const blob = await res.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1] ?? '');
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }
    if (uri.startsWith('http://') || uri.startsWith('https://')) {
      const downloaded = await File.downloadFileAsync(uri, Paths.cache);
      const base64 = await downloaded.base64();
      downloaded.delete();
      return base64;
    }
    return await new File(uri).base64();
  } catch (e) {
    console.warn('uriToBase64 failed for offer PDF logo:', e);
    return null;
  }
}

function buildHtml(
  offer: Pick<Offer, 'title' | 'description' | 'cost' | 'delivery_days' | 'offer_kind' | 'line_items' | 'created_at'>,
  recipientName: string,
  professional: Profile & { professional_terms?: string | null; business_logo_url?: string | null; business_name?: string | null; show_business_name?: boolean | null },
  logoBase64: string | null,
): string {
  const businessName = professional.show_business_name && professional.business_name?.trim()
    ? professional.business_name.trim()
    : professional.full_name?.trim() || '';

  const businessDetailLines: string[] = [];
  const addressParts = [professional.business_address?.trim(), professional.business_postal_code?.trim(), professional.business_city?.trim()].filter(Boolean);
  if (addressParts.length) businessDetailLines.push(addressParts.join(', '));
  if (professional.business_vat_number?.trim()) businessDetailLines.push(`ΑΦΜ: ${professional.business_vat_number.trim()}`);
  if (professional.business_phone?.trim()) businessDetailLines.push(`Τηλ: ${professional.business_phone.trim()}`);
  if (professional.business_email?.trim()) businessDetailLines.push(professional.business_email.trim());
  const businessDetailsHtml = businessDetailLines.length
    ? `<div class="businessDetails">${businessDetailLines.map(escapeHtml).join(' · ')}</div>`
    : '';

  const professionalTerms = professional.professional_terms?.trim() || '';

  // Όλοι οι τρόποι πληρωμής που έχει δηλώσει ο επαγγελματίας στο προφίλ του —
  // ίδια λογική/πεδία με το "Τρόποι Πληρωμής" στο portfolio του.
  const paymentRows: string[] = [];
  const ibans = (professional as unknown as { payment_ibans?: { bank: string | null; iban: string }[] | null }).payment_ibans;
  for (const entry of ibans ?? []) {
    if (!entry?.iban?.trim()) continue;
    paymentRows.push(`<div class="paymentRow"><strong>${escapeHtml(entry.bank?.trim() || 'IBAN')}:</strong> ${escapeHtml(entry.iban.trim())}</div>`);
  }
  const paymentIris = (professional as unknown as { payment_iris?: string | null }).payment_iris?.trim();
  if (paymentIris) paymentRows.push(`<div class="paymentRow"><strong>IRIS:</strong> ${escapeHtml(paymentIris)}</div>`);
  const paymentRevolut = (professional as unknown as { payment_revolut_link?: string | null }).payment_revolut_link?.trim();
  if (paymentRevolut) paymentRows.push(`<div class="paymentRow"><strong>Revolut:</strong> ${escapeHtml(paymentRevolut)}</div>`);
  const paymentViva = (professional as unknown as { payment_viva_link?: string | null }).payment_viva_link?.trim();
  if (paymentViva) paymentRows.push(`<div class="paymentRow"><strong>Viva Wallet:</strong> ${escapeHtml(paymentViva)}</div>`);
  const paymentMethodsHtml = paymentRows.length
    ? `<div class="paymentMethods">
      <div class="termsLabel">Τρόποι Πληρωμής</div>
      ${paymentRows.join('')}
    </div>`
    : '';

  const termsPageHtml = (professionalTerms || paymentMethodsHtml)
    ? `<div class="termsPage">
    <div class="termsPageHeader">
      ${logoBase64 ? `<img src="data:image/png;base64,${logoBase64}" style="max-width:120px;max-height:80px;object-fit:contain;margin-bottom:8px;" />` : ''}
      <div class="businessName">${escapeHtml(businessName)}</div>
      ${businessDetailsHtml}
    </div>
    ${professionalTerms ? `<div class="terms">
      <div class="termsLabel">Όροι &amp; Προϋποθέσεις ${escapeHtml(businessName)}</div>
      <div class="termsText">${escapeHtml(professionalTerms)}</div>
    </div>` : ''}
    ${paymentMethodsHtml}
  </div>`
    : '';

  const logoHtml = logoBase64
    ? `<img src="data:image/png;base64,${logoBase64}" style="max-width:120px;max-height:80px;object-fit:contain;margin-bottom:8px;" />`
    : '';

  let itemsHtml = '';
  let totalAmount: number | null = offer.cost ?? null;
  if (offer.offer_kind === 'goods' && offer.line_items && offer.line_items.length > 0) {
    const items = offer.line_items as OfferLineItem[];
    const totals = computeLineItemsTotals(items);
    totalAmount = totals.finalTotal;
    const rows = items.map((it) => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e5e5;">${escapeHtml(it.description)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e5e5;text-align:center;">${it.quantity}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e5e5;text-align:right;">${formatAmount(it.unitPrice)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e5e5;text-align:right;">${formatAmount(lineItemTotal(it))}</td>
      </tr>`).join('');
    itemsHtml = `
      <table style="width:100%;border-collapse:collapse;margin-top:12px;font-size:13px;">
        <thead>
          <tr style="background:#f5f5f5;">
            <th style="padding:6px 8px;text-align:left;">Περιγραφή</th>
            <th style="padding:6px 8px;text-align:center;">Ποσ.</th>
            <th style="padding:6px 8px;text-align:right;">Τιμή</th>
            <th style="padding:6px 8px;text-align:right;">Σύνολο</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div style="text-align:right;margin-top:10px;font-size:14px;font-weight:700;">
        Σύνολο: ${formatAmount(totals.finalTotal)}
      </div>`;
  } else {
    itemsHtml = `
      <div style="margin-top:12px;font-size:15px;">
        ${offer.cost != null ? `<div><strong>Κόστος:</strong> ${formatAmount(offer.cost)}</div>` : ''}
        ${offer.delivery_days != null ? `<div style="margin-top:4px;"><strong>Παράδοση:</strong> ${offer.delivery_days} ημέρες</div>` : ''}
      </div>`;
  }

  const professionalPersonalName = professional.full_name?.trim() || businessName;
  let commitmentText =
    `Ο ${recipientName} δεσμεύεται ότι θα εξοφλήσει το συμφωνηθέν ποσό` +
    (totalAmount != null ? ` των ${formatAmount(totalAmount)}` : '') +
    ` στον επαγγελματία, καθώς και ο ${professionalPersonalName} δεσμεύεται να παραδώσει/ολοκληρώσει τα συμφωνηθέντα/αναγραφόμενα στην παρούσα προσφορά.`;
  if (professionalTerms) {
    commitmentText += ` Ο ${recipientName} ανέγνωσε και αποδέχεται τους όρους και προϋποθέσεις της επιχείρησης ${businessName}.`;
  }
  const commitmentHtml = `<div class="commitment">${escapeHtml(commitmentText)}</div>`;

  return `<!DOCTYPE html>
<html lang="el">
<head>
<meta charset="utf-8" />
<style>
  body { font-family: -apple-system, 'Segoe UI', Roboto, sans-serif; color: #1a1a1a; padding: 32px; }
  .header { border-bottom: 2px solid #C9A843; padding-bottom: 16px; margin-bottom: 20px; }
  .businessName { font-size: 18px; font-weight: 700; }
  .businessDetails { font-size: 11px; color: #666; margin-top: 4px; }
  .badge { display:inline-block; background:#0D1B2A; color:#C9A843; font-size:11px; font-weight:700; letter-spacing:0.5px; padding:4px 10px; border-radius:12px; margin-bottom:10px; }
  .recipient { font-size: 15px; margin: 16px 0; padding: 12px 16px; background:#f9f7f0; border-left:3px solid #C9A843; }
  .title { font-size: 20px; font-weight: 700; margin-top: 20px; }
  .description { font-size: 14px; color: #444; margin-top: 8px; white-space: pre-wrap; }
  .commitment { margin-top: 16px; padding: 12px 16px; background: #f4f4f4; border-left: 3px solid #0D1B2A; font-size: 13px; color: #333; line-height: 1.5; }
  .termsPage { page-break-before: always; padding-top: 8px; }
  .termsPageHeader { border-bottom: 2px solid #C9A843; padding-bottom: 16px; margin-bottom: 20px; }
  .terms { margin-top: 20px; padding: 14px 16px; background: #f9f7f0; border-left: 3px solid #C9A843; }
  .termsLabel { font-size: 12px; font-weight: 700; color: #0D1B2A; margin-bottom: 6px; }
  .termsText { font-size: 13px; color: #444; white-space: pre-wrap; line-height: 1.5; }
  .paymentMethods { margin-top: 20px; padding: 14px 16px; background: #f4f4f4; border-left: 3px solid #0D1B2A; }
  .paymentRow { font-size: 13px; color: #333; line-height: 1.6; word-break: break-all; }
  .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e5e5e5; font-size: 11px; color: #888; }
</style>
</head>
<body>
  <div class="header">
    <span class="badge">ΠΡΟΣΦΟΡΑ</span>
    ${logoHtml}
    <div class="businessName">${escapeHtml(businessName)}</div>
    ${businessDetailsHtml}
  </div>

  <div class="recipient">Προσφορά προς: <strong>${escapeHtml(recipientName)}</strong></div>

  <div class="title">${escapeHtml(offer.title)}</div>
  ${offer.description ? `<div class="description">${escapeHtml(offer.description)}</div>` : ''}

  ${itemsHtml}

  ${commitmentHtml}

  <div class="footer">
    Ημερομηνία: ${formatDate(offer.created_at)} · Δημιουργήθηκε μέσω Χειραψία
  </div>

  ${termsPageHtml}
</body>
</html>`;
}

export async function exportOfferPdf(
  offer: Pick<Offer, 'title' | 'description' | 'cost' | 'delivery_days' | 'offer_kind' | 'line_items' | 'created_at'>,
  recipientName: string,
  professional: Profile & { professional_terms?: string | null; business_logo_url?: string | null; business_name?: string | null; show_business_name?: boolean | null },
): Promise<void> {
  let logoBase64: string | null = null;
  if (professional.business_logo_url) {
    logoBase64 = await uriToBase64(professional.business_logo_url);
  }

  const html = buildHtml(offer, recipientName, professional, logoBase64);

  if (Platform.OS === 'web') {
    const win = typeof window !== 'undefined' ? window.open('', '_blank') : null;
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 400);
    } else {
      await Print.printAsync({ html });
    }
    return;
  }

  const { uri } = await Print.printToFileAsync({ html });
  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(uri, {
      mimeType: 'application/pdf',
      dialogTitle: 'Προσφορά',
      UTI: 'com.adobe.pdf',
    });
  }
}
